// IE Hub - App Shell Component
// Main application shell with providers and layout

import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

// Providers
import ThemeProvider from "./providers/ThemeProvider.tsx";
import EngineProvider from "./providers/EngineProvider.tsx";
import AlertProvider from "./providers/AlertProvider.tsx";
import { HelpProvider } from "@/shared/help/HelpProvider";
import {
  UNAUTHORIZED_EVENT,
  type UnauthorizedEventDetail,
} from "@/shared/constants/events";

// UI Components
import { Box, CssBaseline } from "@ui/index";

// Hooks
import { useScrollToTop } from "../shared/hooks/useScrollToTop.ts";
import { useAuth } from "../shared/state/store";

import { ConfirmDialogHost } from "@/components/ConfirmDialog";
import { emitGlobalAlert } from "@/shared/lib/alertEvents";
import { consumeLogoutSource } from "@/shared/lib/logoutSource";
import SessionExpiryManager from "./SessionExpiryManager";
import { initMarketingTags, trackPageView } from "@/shared/tracking/initMarketingTags";
import SimpleLoadingScreen from "@/components/SimpleLoadingScreen";
import { useDOMErrorSuppression } from "@/shared/hooks/useDOMErrorSuppression";
import { setClarityMaskAll } from "@/shared/logging/clarity";

export const PUBLIC_ROUTE_PREFIXES: readonly string[] = ["/auth"];
const DEFAULT_UNAUTHORIZED_MESSAGE =
  "Your session has expired. Please sign in again.";

// Segments rendered in ALL CAPS instead of Title Case.
const TITLE_ACRONYMS = new Set([
  "uw", "ob", "obq", "kyc", "faqs", "pwa", "api", "ai", "id", "dd", "sso",
]);

// Strips path params like Mongo ObjectIds, UUIDs, numeric IDs, and JWT-style tokens
// so they don't leak into the browser tab title.
const isIdLikeSegment = (segment: string): boolean => {
  if (/^[0-9a-f]{20,}$/i.test(segment)) return true;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)) return true;
  if (/^\d+$/.test(segment)) return true;
  if (segment.length > 30) return true;
  return false;
};

const formatTitleSegment = (segment: string): string =>
  segment
    .split("-")
    .map((word) => {
      if (!word) return "";
      if (TITLE_ACRONYMS.has(word.toLowerCase())) return word.toUpperCase();
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");

const buildPageTitle = (pathname: string): string => {
  const parts = pathname
    .split("/")
    .filter(Boolean)
    .filter((s) => !isIdLikeSegment(s))
    .map(formatTitleSegment);
  return parts.length > 0 ? (parts[parts.length - 1] as string) : "Dashboard";
};

function AppShell(): JSX.Element {
  useDOMErrorSuppression(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { initializeAuth, isLoading, isAuthenticated, logout } = useAuth();
  const [authInitialized, setAuthInitialized] = useState(false);
  const unauthorizedHandledRef = useRef(false);

  const isProtectedRoute = useMemo(() => {
    const pathname = location.pathname;
    return !PUBLIC_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix));
  }, [location.pathname]);

  // Initialize auth on app startup
  useEffect(() => {
    const initAuth = async () => {
      await initializeAuth();
      setAuthInitialized(true);
    };
    initAuth();
  }, [initializeAuth]);

  // Initialize GTM/GA4/Meta Pixel once (safe to call multiple times)
  useEffect(() => {
    initMarketingTags();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return () => undefined;
    }

    const listener = (event: Event) => {
      if (!isProtectedRoute) {
        return;
      }

      const logoutSource = consumeLogoutSource();
      if (logoutSource === "manual" || logoutSource === "account_locked") {
        return;
      }

      if (unauthorizedHandledRef.current) {
        return;
      }

      const unauthorizedEvent = event as CustomEvent<
        UnauthorizedEventDetail | undefined
      >;
      const reason =
        unauthorizedEvent.detail?.reason ?? DEFAULT_UNAUTHORIZED_MESSAGE;

      unauthorizedHandledRef.current = true;

      void (async () => {
        try {
          await logout();
        } catch (error) {
          console.warn("Failed to logout after unauthorized response", error);
        }

        // Show a single, high-severity session expiry message (red)
        emitGlobalAlert({
          severity: "error",
          message: reason,
          autoHide: true,
          duration: 4000,
        });

        navigate("/auth", {
          replace: true,
          state: {
            from: `${location.pathname}${location.search}`,
          },
        });
      })();
    };

    window.addEventListener(UNAUTHORIZED_EVENT, listener as EventListener);
    return () => {
      window.removeEventListener(UNAUTHORIZED_EVENT, listener as EventListener);
    };
  }, [
    isProtectedRoute,
    location.pathname,
    location.search,
    logout,
    navigate,
  ]);

  useEffect(() => {
    if (isAuthenticated) {
      unauthorizedHandledRef.current = false;
    }
  }, [isAuthenticated]);

  // Scroll to top on route change
  useScrollToTop(location.pathname);

  // Update page title based on route
  useEffect(() => {
    document.title = buildPageTitle(location.pathname);
  }, [location.pathname]);

  // Track SPA virtual page views on route changes (HashRouter friendly)
  useEffect(() => {
    trackPageView({
      pagePath: `${location.pathname}${location.search}`,
      pageTitle: buildPageTitle(location.pathname),
      pageLocation: typeof window !== "undefined" ? window.location.href : "",
    });
  }, [location.pathname, location.search]);

  // Mask all on-screen content from Microsoft Clarity session recordings while
  // the user is on an authenticated (non-/auth) route, so the admin team never
  // sees customer PII in replays. See setClarityMaskAll for why this targets
  // <body>. Public /auth routes stay unmasked so login/funnel recordings remain
  // usable. main.tsx applies the same mask synchronously at startup to cover
  // direct deep-links before Clarity's recorder loads.
  useEffect(() => {
    setClarityMaskAll(isProtectedRoute);
  }, [isProtectedRoute]);

  return (
    <ThemeProvider>
      <CssBaseline />
      {/* Ensure loading UI is always themed (prevents default MUI blue fallback). */}
      {!authInitialized || isLoading ? (
        <SimpleLoadingScreen title="Loading..." />
      ) : (
        <HelpProvider>
          <EngineProvider>
            <AlertProvider>
              <ConfirmDialogHost />
              <SessionExpiryManager />
              <Box
                sx={{
                  minHeight: "100vh",
                  display: "flex",
                  flexDirection: "column",
                  backgroundColor: "background.default",
                }}
              >
                {/* Global loading indicator, navigation, etc. could go here */}

                {/* Main content area */}
                <Box
                  component="main"
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Suspense fallback={<SimpleLoadingScreen title="Loading..." />}>
                    <Outlet />
                  </Suspense>
                </Box>

                {/* Global footer, modals, etc. could go here */}
              </Box>
            </AlertProvider>
          </EngineProvider>
        </HelpProvider>
      )}
    </ThemeProvider>
  );
}

export default AppShell;
